// MyEmployeesPage.tsx - FULLY UPGRADED
import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTable, Column, CellProps } from 'react-table';
import { Table } from 'reactstrap';
import { useNavigate } from 'react-router-dom'; // Import useNavigate for row clicks
import MainCard from '../../../component/MainCard/MainCard';
import useLoadingIndicator from '../../../component/Loader/Loader';
import ShowEntries from '../../../component/ShowEntries/ShowEntries';
import * as actions from '../../../Redux/Actions';
import SearchBar from '../../../component/SearchBar/SearchBar'; // Import SearchBar
import './MyEmployeesPage.scss'; // We will create this SCSS file

// Define the NEW rich data structure we get from the API
interface EnrichedEmployeeData {
    _id: string;
    employeeId: string;
    candidateName: string;
    lastname: string;
    emailId: string;
    designation: string;
    department: string;
    dateOfJoining: string;
    status: string;
    openTasks: number;
    overdueTasks: number;
}

const MyEmployeesPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate(); // Hook for navigation
    const { loadingComponent, showLoadingIndicator, hideLoadingIndicator } = useLoadingIndicator();

    const { getHrbpMyEmployees } = useSelector((state: any) => state.employeeReducer);

    // State for pagination and search
    const [pagination, setPagination] = useState({ page: 1, limit: 10 });
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    // Debounce search input to avoid excessive API calls
    useEffect(() => {
        const timerId = setTimeout(() => {
            setDebouncedSearch(search);
            setPagination(p => ({ ...p, page: 1 })); // Reset to page 1 on new search
        }, 500);
        return () => clearTimeout(timerId);
    }, [search]);

    useEffect(() => {
        showLoadingIndicator();
        const filters = { ...pagination, search: debouncedSearch };
        dispatch(actions.getHrbpMyEmployeesAction(filters, () => {
            hideLoadingIndicator();
        }));
    }, [dispatch, pagination, debouncedSearch]);

    const tableData = useMemo(() => getHrbpMyEmployees?.data?.data || [], [getHrbpMyEmployees]);
    const paginationData = useMemo(() => ({
        currentPage: getHrbpMyEmployees?.data?.currentPage || 1,
        totalPages: getHrbpMyEmployees?.data?.totalPages || 1,
        totalRecords: getHrbpMyEmployees?.data?.totalRecords || 0,
    }), [getHrbpMyEmployees]);

    // Handle clicking a row to navigate to the planner, pre-filtered
    const handleRowClick = (employee: EnrichedEmployeeData) => {
        const employeeName = `${employee.candidateName} ${employee.lastname || ''}`.trim();
        // Navigate to the planner and pass the employee's name as a search query
        navigate(`/hrbp-tracker?search=${encodeURIComponent(employeeName)}`);
    };

    const columns: readonly Column<EnrichedEmployeeData>[] = useMemo(() => [

        {
            Header: 'S.No.',
            id: 'serialNumber', 
            Cell: ({ row }: CellProps<EnrichedEmployeeData>) => {
                return <>{(pagination.page - 1) * pagination.limit + row.index + 1}</>;
            }
        },

        { Header: 'E-Code', accessor: 'employeeId' },
        {
            Header: 'Full Name',
            id: 'fullName',
            Cell: ({ row }: CellProps<EnrichedEmployeeData>) => (
                <>{`${row.original.candidateName} ${row.original.lastname || ''}`}</>
            )
        },
        { Header: 'Designation', accessor: 'designation' },
        { Header: 'Department', accessor: 'department' },
        {
            Header: 'Open Tasks',
            accessor: 'openTasks',
            Cell: ({ value }) => <span className="task-count">{value}</span>
        },
        {
            Header: 'Overdue',
            accessor: 'overdueTasks',
            Cell: ({ value }) => (
                <span className={`task-count ${value > 0 ? 'overdue' : ''}`}>{value}</span>
            )
        },
        {
            Header: 'Status',
            accessor: 'status',
            Cell: ({ value }) => (
                <span className={`status-pill status-${String(value).toLowerCase()}`}>{value}</span>
            )
        },
    ], [navigate]);

    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({ columns, data: tableData });

    const handlePageChange = (newPage: number) => {
        if (newPage > 0 && newPage <= paginationData.totalPages) {
            setPagination(prev => ({ ...prev, page: newPage }));
        }
    };

    const handlePageSizeChange = (newSize: number) => {
        setPagination({ page: 1, limit: newSize });
    };

    return (
        <MainCard>
            {loadingComponent()}
            <div className="p-4 my-employees-page">
                <div className="page-header">
                    <div>
                        <h2>My Assigned Employees</h2>
                        <p className="subtitle">A list of all employees for whom you are the assigned HRBP.</p>
                    </div>
                </div>

                <div className="filter-bar">
                    <SearchBar filter={search} setFilter={setSearch} placeholder="Search by name, E-code..." onSearchClicked={() => {}}  />
                </div>

                <div className="table-wrapper">
                    <Table hover {...getTableProps()} className="employee-table">
                        <thead>
                            {headerGroups.map(hg => (
                                <tr {...hg.getHeaderGroupProps()}>
                                    {hg.headers.map(col => <th {...col.getHeaderProps()}>{col.render('Header')}</th>)}
                                </tr>
                            ))}
                        </thead>
                        <tbody {...getTableBodyProps()}>
                            {rows.length > 0 ? (
                                rows.map(row => {
                                    prepareRow(row);
                                    return (
                                        <tr {...row.getRowProps()} onClick={() => handleRowClick(row.original)} className="clickable-row">
                                            {row.cells.map(cell => <td {...cell.getCellProps()}>{cell.render('Cell')}</td>)}
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={columns.length} className="text-center p-5">No employees found.</td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
                <div className="d-flex justify-content-between align-items-center mt-3">
                    <ShowEntries pageSize={pagination.limit} setPageSize={handlePageSizeChange} totalEntries={paginationData.totalRecords} />
                    <nav>
                        <ul className="pagination">
                            <li className={`page-item ${paginationData.currentPage === 1 ? 'disabled' : ''}`}>
                                <a className="page-link" href="#" onClick={() => handlePageChange(paginationData.currentPage - 1)}>‹</a>
                            </li>
                            {Array.from({ length: paginationData.totalPages }, (_, i) => i + 1).map(number => (
                                <li key={number} className={`page-item ${paginationData.currentPage === number ? 'active' : ''}`}>
                                    <a className="page-link" href="#" onClick={() => handlePageChange(number)}>{number}</a>
                                </li>
                            ))}
                            <li className={`page-item ${paginationData.currentPage === paginationData.totalPages ? 'disabled' : ''}`}>
                                <a className="page-link" href="#" onClick={() => handlePageChange(paginationData.currentPage + 1)}>›</a>
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>
        </MainCard>
    );
};

export default MyEmployeesPage;